import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { RequestService } from 'src/app/services/request.service';
import { take, takeUntil } from 'rxjs/operators';
import { dynamicLocaleCompareWithOptions } from 'src/app/helper/sortFunctions';
import { SimpleInputDialog } from 'src/app/Modal/simple-input.modal';
import { AddWagonVariantComponent } from '../add-wagon-variant/add-wagon-variant.component';
import { ModalDialog } from 'src/app/Modal/modal';

@Component({
  selector: 'app-train-list-view',
  templateUrl: './train-list-view.component.html',
  styleUrls: ['./train-list-view.component.scss']
})
export class TrainListViewComponent implements OnInit {
  private unscubscribe$ = new Subject<void>();
  public hasDeletedVariantInAnyTrainConfiguration: boolean;
  trainList: any[] = []
  wagonList: any[] = []
  private routeSub: Subscription;
  private selectedProjectId: number;
  train = {
    addButton: 'Add New Train',
    variantWarning: 'Atleast one train includes a deleted variant',
    deleteTrainDialog: 'Do you really want to delete this train?',
    deleteTrainPartDialog: 'Do you really want to delete this wagon?'
  }

  constructor(private route: ActivatedRoute, private dialog: MatDialog, private requestService: RequestService) { }

  ngOnInit() {
    this.routeSub = this.route.params.subscribe(params => {
      this.selectedProjectId = parseInt(params['id'].toString());

      this.loadTrains()
      this.loadWagons();
    });
  }

  loadTrains() {
    this.requestService.getTrains(this.selectedProjectId)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(resp => {
        this.setTrainList(resp);
      });
  }

  loadWagons() {
    this.requestService.getWagons(this.selectedProjectId)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(resp => {
        this.setWagonList(resp);
      });
  }

  setWagonList(wagonList: any[]) {
    if (wagonList) {
      this.wagonList = wagonList.sort((a, b) => a.index - b.index);;
    }
  }

  setTrainList(trains: any[]) {
    this.trainList = [];

    if (trains) {
      this.trainList = trains.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
    }

    this.checkForDeleteVariantsInsideTheTrains();
  }

  private checkForDeleteVariantsInsideTheTrains() {
    this.hasDeletedVariantInAnyTrainConfiguration = false;

    for (let index = 0; index < this.trainList.length; index++) {
      const train = this.trainList[index];

      if (this.hasDeletedVariantInAnyTrainConfiguration) {
        break;
      }

      if (train.trainConfiguration) {
        for (let index = 0; index < train.trainConfiguration.length; index++) {
          const trainPart = train.trainConfiguration[index];
          if (trainPart.variantId && trainPart.image == null) {
            this.hasDeletedVariantInAnyTrainConfiguration = true;
            break;
          }
        }
      }
    }
  }

  openCreateTrainModal() {
    const dialogRef = this.dialog.open(SimpleInputDialog, {
      data: { title: 'Create train', layoutname: 'Train name' }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result.state) {
          this.writeTrain({ projectId: this.selectedProjectId, name: result.layoutname, trainConfiguration: [] });
        }
      });
  }

  private writeTrain(data: any) {
    this.requestService.writeTrain(data)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(resp => {
        this.setTrainList(resp);
      });
  }

  // train actions
  openDeleteTrainModal(trainId: number) {
    const dialogRef = this.dialog.open(ModalDialog, {
      data: {
        title: 'Warning',
        message: this.train.deleteTrainDialog,
        showCancel: true,
        cancelButtonText: 'Cancel',
        saveButtonText: 'Okay'
      }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.deleteTrain(trainId);
        } else {
          // do nothing just close the modal
        }
      });
  }

  private deleteTrain(trainId: number) {
    this.requestService.deleteTrain(trainId)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe((resp: any) => {
        this.setTrainList(resp);
      }, error => {
        //
      });
  }

  copyTrain(data: any) {
    this.requestService.copyTrain(data)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe((resp: any) => {
        this.setTrainList(resp);
      }, error => {
        //
      });
  }

  editTrain(train: any) {
    const dialogRef = this.dialog.open(SimpleInputDialog, {
      data: { title: 'Edit train', layoutname: train.name }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result.state) {
          const data = { id: train.id, projectId: this.selectedProjectId, name: result.layoutname, trainConfiguration: train.trainConfiguration }
          this.writeTrain(data);
        } else {
          // close
        }
      });
  }

  openAddTrainPartDialog(data: any) {
    const dialogRef = this.dialog.open(AddWagonVariantComponent, {
      width: '100vw',
      data: { train: data.train, index: data.index, wagonList: this.wagonList }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.addTrainPart(result);
        } else {
          // close
        }
      });
  }

  private addTrainPart(result: any) {
    const data = { id: result.id, projectId: this.selectedProjectId, name: result.name, trainConfiguration: result.trainConfiguration }
    this.writeTrain(data);
  }

  openDeleteTrainPartModal(data: any) {
    const dialogRef = this.dialog.open(ModalDialog, {
      data: {
        title: 'Warning',
        message: this.train.deleteTrainPartDialog,
        showCancel: true,
        cancelButtonText: 'Cancel',
        saveButtonText: 'Okay'
      }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.deleteTrainPart(data);
        } else {
          // do nothing just close the modal
        }
      });
  }

  private deleteTrainPart(data: any) {
    const train = this.getTrainFromList(data.trainId);

    if (train) {
      train.trainConfiguration = train.trainConfiguration.filter((tc: any) => tc.id !== data.wagonId);

      // update the index property after removing the train part from the configuration
      for (let index = 0; index < train.trainConfiguration.length; index++) {
        const trainPart = train.trainConfiguration[index];
        trainPart.index = index;
      }

      this.writeTrain(train);
    }
  }

  private getTrainFromList(trainId: number) {
    return this.trainList.find((t: any) => t.id === trainId);
  }

  swapTrainPartPlaces(data: any) {
    const train = this.getTrainFromList(data.trainId);

    if (train) {
      train.trainConfiguration[data.index + 1].index = data.index;
      train.trainConfiguration[data.index].index = data.index + 1;

      const updatedTrain = { id: train.id, projectId: this.selectedProjectId, name: train.name, trainConfiguration: train.trainConfiguration }
      this.writeTrain(updatedTrain);
    }
  }

  rotateTrainPart(data: any) {
    const train = this.getTrainFromList(data.trainId);

    if (train) {
      const wagonConfig = train.trainConfiguration.find((tc: any) => tc.id === data.wagonId);
      wagonConfig.rotated = !wagonConfig.rotated;
      this.writeTrain(train);
    }
  }

  editTrainPart(updatedTrainPart: any) {
    const dialogRef = this.dialog.open(SimpleInputDialog, {
      data: { title: 'Edit train', layoutname: updatedTrainPart.wagon.name }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result.state) {
          const train = this.getTrainFromList(updatedTrainPart.trainId);

          if (train) {
            const wagonConfig = train.trainConfiguration.find((tc: any) => tc.id === updatedTrainPart.wagon.id);
            wagonConfig.name = result.layoutname;
            this.writeTrain(train);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.routeSub.unsubscribe();
    this.unscubscribe$.next();
    this.unscubscribe$.complete();
  }
}
